import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Palette, Download, ExternalLink, Copy, Check } from "lucide-react";

const PUBLIC = process.env.PUBLIC_URL || "";
const asset = (f) => `${PUBLIC}/${f}`;

const ASSETS = [
  { name: "Logo primario", file: "brand/markoo-logo-primary.svg", note: "Su sfondi chiari — uso principale" },
  { name: "Logo negativo", file: "brand/markoo-logo-negative.svg", note: "Su sfondi scuri" },
  { name: "Icona / Monogramma", file: "brand/markoo-icon.svg", note: "App, avatar, watermark" },
  { name: "Favicon", file: "favicon.svg", note: "Sito web / tab del browser" },
  { name: "Avatar social", file: "brand/markoo-social-avatar.svg", note: "Foto profilo 1:1 (IG, FB, WA)" },
  { name: "Cover / Banner", file: "brand/markoo-cover.svg", note: "Copertina social e sito", wide: true },
  { name: "Badge SIMMAMMA", file: "brand/simmamma-badge.svg", note: "Sotto-brand SIM" },
];

const PALETTE = [
  { name: "Blu Markoo", hex: "#2F6BFF" },
  { name: "Blu profondo", hex: "#12327A" },
  { name: "Arancio SIMMAMMA", hex: "#F97316" },
  { name: "Scuro UI", hex: "#0A0E17" },
  { name: "Bianco", hex: "#FFFFFF" },
];

export default function KitMarketing() {
  const [bg, setBg] = useState("dark");
  const [copied, setCopied] = useState("");
  const preview = bg === "white" ? "#ffffff" : "#0A0E17";

  const copyHex = (hex) => { navigator.clipboard.writeText(hex); setCopied(hex); toast.success(`${hex} copiato`); setTimeout(() => setCopied(""), 1200); };

  return (
    <div className="space-y-6" data-testid="kit-marketing-page">
      <div>
        <div className="flex items-center gap-2"><Palette className="w-4 h-4 text-primary" /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Materiali</span></div>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Kit Marketing</h1>
        <p className="text-sm text-muted-foreground mt-1">Loghi e declinazioni del brand Markoo Place, pronti da scaricare e usare per la tua vetrina, i social e la stampa. File SVG vettoriali (qualità infinita).</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted-foreground">Sfondo anteprima:</span>
        <div className="inline-flex rounded-lg border border-border overflow-hidden" data-testid="kit-bg-toggle">
          <button onClick={() => setBg("dark")} className={`px-4 py-1.5 text-sm ${bg === "dark" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground"}`} data-testid="kit-bg-dark">Scuro</button>
          <button onClick={() => setBg("white")} className={`px-4 py-1.5 text-sm ${bg === "white" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground"}`} data-testid="kit-bg-white">Bianco</button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ASSETS.map((a) => (
          <Card key={a.file} className={`p-4 flex flex-col ${a.wide ? "sm:col-span-2 lg:col-span-3" : ""}`} data-testid={`kit-asset-${a.file}`}>
            <div className="rounded-xl border border-border flex items-center justify-center p-6 min-h-[160px]" style={{ background: preview }}>
              <img src={asset(a.file)} alt={a.name} className={`${a.wide ? "max-h-40" : "max-h-24"} w-auto max-w-full object-contain`} />
            </div>
            <div className="mt-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-head font-semibold truncate">{a.name}</h3>
                <p className="text-xs text-muted-foreground">{a.note}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <a href={asset(a.file)} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost" title="Apri" data-testid={`kit-open-${a.file}`}><ExternalLink className="w-4 h-4" /></Button></a>
                <a href={asset(a.file)} download><Button size="icon" variant="outline" title="Scarica" data-testid={`kit-download-${a.file}`}><Download className="w-4 h-4" /></Button></a>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div>
        <h2 className="font-head text-lg font-semibold mb-3">Palette colori</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {PALETTE.map((c) => (
            <button key={c.hex} onClick={() => copyHex(c.hex)} className="rounded-xl border border-border overflow-hidden text-left group" data-testid={`kit-color-${c.hex}`}>
              <div className="h-16" style={{ background: c.hex }} />
              <div className="p-2">
                <p className="text-sm font-medium">{c.name}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">{c.hex}{copied === c.hex ? <Check className="w-3 h-3 text-teal-500" /> : <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100" />}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Card className="p-4 text-sm text-muted-foreground" data-testid="kit-usage">
        <p className="font-medium text-foreground mb-1">Linee guida rapide</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Mantieni un margine di rispetto attorno al logo pari all'altezza di un "occhio".</li>
          <li>Usa il logo primario su sfondi chiari e la versione negativa su sfondi scuri.</li>
          <li>Non deformare, ruotare o ricolorare il logo. Gli SVG sono scalabili senza perdita.</li>
        </ul>
      </Card>
    </div>
  );
}
